import {ActionReducerMap, createFeatureSelector, createSelector} from '@ngrx/store';

import * as fromRoot from '../../reducers';
import * as fromAfbApi from '../../afb-api/reducers';
import * as fromDashboard from '../../dashboard/reducers';
import * as fromFloorPlanView from './floor-plan-view.reducer';
import {Floor, FloorPlanDevice} from '../../afb-api/models/floor';
import {PolledAirDatum} from '../../afb-api/models/air-data';
import {DeviceMarkerWithPolledAirDatum} from '../../floor-plan-shared/models/device-marker';
import {Device} from '../../afb-api/models/device';
import {AirComponentType} from '../../afb-api/enums/air-component-type.enum';
import {SelfOrganizationUser} from '../../afb-api/models/organization-user';
import {TemperatureUnit} from '../../shared';

export interface FloorPlanState {
  floorPlanView: fromFloorPlanView.State;
}

export interface State extends fromRoot.State {
  floorPlan: FloorPlanState;
}

export const reducers: ActionReducerMap<FloorPlanState> = {
  floorPlanView: fromFloorPlanView.reducer
};

export const floorPlanState = createFeatureSelector<FloorPlanState>('floor-plan');

/**
 * Floor Plan View State
 */
export const floorPlanViewState = createSelector(
  floorPlanState,
  (state) => state.floorPlanView
);

export const selectedFloorId = createSelector(
  floorPlanViewState,
  fromFloorPlanView.selectedFloorId
);
export const selectedAirComponent = createSelector(
  floorPlanViewState,
  fromFloorPlanView.selectedAirComponent
);

export const selectedFloor = createSelector(
  selectedFloorId,
  fromAfbApi.floorEntities,
  (_selectedFloorId: string, floorEntities: { [floorid: string]: Floor }) => floorEntities[_selectedFloorId]
);
export const selectedFloorPlanDevices = createSelector(
  selectedFloor,
  (_selectedFloor: Floor) => (_selectedFloor != null && _selectedFloor.floorPlan != null && _selectedFloor.floorPlan.devices) ?
    _selectedFloor.floorPlan.devices : null
);
export const selectedFloorPlanDeviceMarkers = createSelector(
  selectedFloorPlanDevices,
  selectedAirComponent,
  fromAfbApi.selfOrganizationUser,
  fromAfbApi.airDataPollEntities,
  fromAfbApi.deviceEntities,
  fromDashboard.selectedDeviceEntities,
  (_selectedFloorPlanDevices: FloorPlanDevice[], _selectedAirComponent: AirComponentType, organizationUser: SelfOrganizationUser,
   polledAirData: { [deviceUuid: string]: PolledAirDatum }, deviceEntities: { [deviceUuid: string]: Device },
   selectedDeviceEntities: { [deviceUuid: string]: Device }) => {
    if (_selectedFloorPlanDevices != null) {
      const colorMap = ['#4FCC53', '#FCD03D', '#F9A91B', '#F78725', '#E73740'];
      const sensorUnitPair = {
        'score': '/100',
        'temp': (organizationUser && organizationUser.tempUnit) === TemperatureUnit.Fahrenheit ? '°F' : '°C',
        'humid': '%',
        'co2': 'ppm',
        'voc': 'ppb',
        'dust': 'µg/m³',
        'pm25': 'µg/m³'
      };

      return _selectedFloorPlanDevices.reduce((acc: DeviceMarkerWithPolledAirDatum[], floorPlanDevice: FloorPlanDevice) => {
        if (floorPlanDevice && floorPlanDevice.deviceUuid && polledAirData[floorPlanDevice.deviceUuid] != null) {
          const scoreColor = polledAirData[floorPlanDevice.deviceUuid].score < 60 ? '#E73740' :
            polledAirData[floorPlanDevice.deviceUuid].score < 80 ? '#F9A91B' : '#4FCC53';
          const value: number = _selectedAirComponent === AirComponentType.Score ?
            polledAirData[floorPlanDevice.deviceUuid].score : polledAirData[floorPlanDevice.deviceUuid].sensors[_selectedAirComponent];
          const color: string = _selectedAirComponent === AirComponentType.Score ?
            scoreColor : (colorMap[polledAirData[floorPlanDevice.deviceUuid].index[_selectedAirComponent]] || '#ACB4BB');

          return [
            ...acc,
            {
              ...floorPlanDevice,
              color,
              value: value != null ? value.toString() : null,
              name: deviceEntities[floorPlanDevice.deviceUuid].deviceName,
              isOnline: polledAirData[floorPlanDevice.deviceUuid].isOnline,
              unit: sensorUnitPair[_selectedAirComponent],
              isSelected: selectedDeviceEntities[floorPlanDevice.deviceUuid] != null
            }
          ];
        } else if (floorPlanDevice && floorPlanDevice.deviceUuid && polledAirData[floorPlanDevice.deviceUuid] == null) {
          return [
            ...acc,
            {
              ...floorPlanDevice,
              color: '#ACB4BB',
              value: null,
              name: deviceEntities[floorPlanDevice.deviceUuid].deviceName,
              isOnline: false,
              unit: sensorUnitPair[_selectedAirComponent],
              isSelected: selectedDeviceEntities[floorPlanDevice.deviceUuid] != null
            }
          ];
        } else {
          return [
            ...acc
          ];
        }
      }, []);
    }
  }
);
